<script setup lang="ts">
import { reactive } from 'vue'
import { NButton, NInput, NSelect, useMessage } from 'naive-ui'
import { useAgentPromptsStore } from '../stores/agent_prompts'
import { promptFactoryButtonProps } from './PromptFactoryButton'

// "Add a prompt" form for the Prompt Library — shown in the editor pane
// when the header's add toggle is open. Owns the create call so the page
// component stays a layout + list coordinator. Stays open after a
// successful add (fields cleared) so several prompts can go in a row.
defineProps<{
  // `''` is the Global sentinel (see PromptLibrary.vue's GLOBAL_SCOPE).
  scopeOptions: Array<{ label: string; value: string }>
}>()

const emit = defineEmits<{ close: [] }>()

const GLOBAL_SCOPE = ''
const promptsStore = useAgentPromptsStore()
const message = useMessage()
const form = reactive({ label: '', text: '', repo_slug: GLOBAL_SCOPE, saving: false })

async function addPrompt(): Promise<void> {
  if (!form.label.trim() || !form.text.trim()) {
    message.error('Label and text are required')
    return
  }
  form.saving = true
  try {
    await promptsStore.create({
      label: form.label.trim(),
      text: form.text.trim(),
      repo_slug: form.repo_slug || null,
    })
    form.label = ''
    form.text = ''
    form.repo_slug = GLOBAL_SCOPE
    message.success('Prompt added')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to add prompt')
  } finally {
    form.saving = false
  }
}
</script>

<template>
  <section
    aria-labelledby="prompt-add-heading"
    class="flex flex-col gap-4 rounded-2xl border border-border-subtle bg-surface-raised p-4 sm:p-5"
    data-testid="prompt-add-form"
  >
    <h2 id="prompt-add-heading" class="m-0 text-lg font-semibold text-ink">New prompt</h2>
    <div class="flex flex-col gap-1.5">
      <label for="prompt-add-label-input" class="font-medium text-ink">Label</label>
      <NInput
        v-model:value="form.label"
        size="large"
        :disabled="form.saving"
        :input-props="{ id: 'prompt-add-label-input' }"
        data-testid="prompt-add-label"
      />
    </div>
    <div class="flex flex-col gap-1.5">
      <label for="prompt-add-text-input" class="font-medium text-ink">Prompt</label>
      <NInput
        v-model:value="form.text"
        type="textarea"
        class="font-mono text-[13px]"
        :autosize="{ minRows: 6, maxRows: 16 }"
        :disabled="form.saving"
        :input-props="{ id: 'prompt-add-text-input' }"
        data-testid="prompt-add-text"
      />
    </div>
    <div class="flex flex-col gap-1.5">
      <span class="font-medium text-ink">Scope</span>
      <NSelect
        v-model:value="form.repo_slug"
        :options="scopeOptions"
        :disabled="form.saving"
        aria-label="Scope"
        class="sm:max-w-72"
        data-testid="prompt-add-scope"
      />
    </div>
    <div class="flex flex-wrap items-center justify-end gap-2.5">
      <button
        type="button"
        class="min-h-11 cursor-pointer rounded-[10px] border border-border-control bg-transparent px-4 text-ink hover:bg-surface-hover sm:min-h-10"
        data-testid="prompt-add-cancel"
        @click="emit('close')"
      >
        Cancel
      </button>
      <NButton
        v-bind="promptFactoryButtonProps"
        class="min-h-11 px-4 font-semibold sm:min-h-10"
        :loading="form.saving"
        data-testid="prompt-add-submit"
        @click="addPrompt"
        >Add prompt</NButton
      >
    </div>
  </section>
</template>
